var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useIsAuthenticated } from './AuthHelper';
import axios from 'axios';
import Modal from '@widget/popups/Modal';
import ProfileImage from '@widget/other/ProfileImage';
import FormField from '@widget/forms/inputs/FormField';
import Input from '@widget/forms/inputs/Input';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import useToast from '@notifications/Toast/useToast';
import { useUXPContext } from '@core/context/useUXPContext';
import { broadcastAuthEvent, redirectToLogin, useAuthBroadcastListener } from '@core/auth/authBroadcast';
const AuthHelperComponent = React.forwardRef((props, ref) => {
    var _a, _b, _c, _d;
    const context = useUXPContext();
    const { interval } = props;
    const toast = useToast();
    // Anonymous sessions have no real user — auth helper must not run.
    // Use the immutable isPublicSession flag; userKey mutates after setPublicCredentials for public dashboards.
    const isAnonymous = context.isPublicSession;
    const [show, setShow] = React.useState(false);
    const [userDetails, setUserDetails] = React.useState(null);
    const [password, setPassword] = React.useState('');
    const isAuthenticated = useIsAuthenticated(context, isAnonymous ? 0 : interval);
    React.useImperativeHandle(ref, () => ({
        show: () => {
            if (!userDetails) {
                redirectToLogin(true, context.loginUrl);
            }
            else {
                setShow(true);
                broadcastAuthEvent({ type: 'auth-required' });
            }
        }
    }));
    React.useEffect(() => {
        if (context.setLoginUIRef) {
            context.setLoginUIRef(ref);
        }
    }, [context, ref]);
    React.useEffect(() => {
        if (isAnonymous)
            return;
        if (context.getUserDetails) {
            context.getUserDetails()
                .then((res) => {
                setUserDetails(res);
            })
                .catch(e => console.error('AUTH HELPER:: Unable to get user details. Exception:', e));
        }
    }, [context, isAnonymous]);
    useAuthBroadcastListener((e) => {
        if (e.type === 'logout') {
            redirectToLogin(false, context.loginUrl);
        }
        else if (e.type === 'auth-required') {
            if (!userDetails)
                redirectToLogin(true, context.loginUrl);
            else
                setShow(true);
        }
        else if (e.type === 'login') {
            setShow(false);
            window.location.reload();
        }
    }, isAnonymous);
    React.useEffect(() => {
        if (isAnonymous)
            return;
        if (!isAuthenticated) {
            if (!userDetails)
                redirectToLogin(true, context.loginUrl);
            else
                setShow(true);
        }
    }, [isAuthenticated, isAnonymous, userDetails]);
    // Clear password when modal closes for security
    React.useEffect(() => {
        if (!show) {
            setPassword('');
        }
    }, [show]);
    function logout() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const baseUrl = ((_a = context.fullAccountUrl) === null || _a === void 0 ? void 0 : _a.replace(/\/$/, '')) || '';
            const url = `${baseUrl}/api/auth/logout`;
            return yield axios.post(url, {}, { withCredentials: true });
        });
    }
    function login() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            // Validate password
            if (!password || password.trim().length === 0) {
                toast.error(((_a = context.$L) === null || _a === void 0 ? void 0 : _a.call(context, 'uxp-core.auth-helper.enter-password')) || 'Please enter your password');
                throw new Error('invalid password');
            }
            // Validate user details
            if (!userDetails || !userDetails.LoginID || ((_b = userDetails.LoginID) === null || _b === void 0 ? void 0 : _b.trim().length) === 0) {
                toast.error(((_c = context.$L) === null || _c === void 0 ? void 0 : _c.call(context, 'uxp-core.auth-helper.user-id-not-found')) || 'User ID not found');
                throw new Error('User id not found');
            }
            // Logout to clear cookies and sessions before login.
            // A failed logout must not stop the re-login: a v4 host has no /api/auth/logout.
            yield logout().catch(e => console.warn('AUTH HELPER:: logout before re-login failed. Continuing.', e));
            const baseUrl = ((_d = context.lucyUrl) === null || _d === void 0 ? void 0 : _d.replace(/\/$/, "")) || '';
            const url = baseUrl + '/Auth/AuthenticateUser';
            let formData = new FormData();
            formData.set('login', userDetails.LoginID);
            formData.set('password', password);
            try {
                const res = yield axios.post(url, formData);
                const token = res.data;
                // Update session first
                yield updateSession(token);
                // Update API key - this triggers parent re-render via onAPIKeyChange
                if (context.updateAPIKey) {
                    context.updateAPIKey(token);
                }
                // Close modal and clear password
                setPassword('');
                setShow(false);
                broadcastAuthEvent({ type: 'login' });
            }
            catch (e) {
                if (((_e = e.response) === null || _e === void 0 ? void 0 : _e.data) === 'Invalid login id or password') {
                    toast.error(((_f = context.$L) === null || _f === void 0 ? void 0 : _f.call(context, 'uxp-core.auth-helper.invalid-password')) || 'Invalid Password. Please enter a valid password');
                    throw new Error('invalid password');
                }
                console.error('AUTH HELPER:: Login exception:', ((_g = e.toJSON) === null || _g === void 0 ? void 0 : _g.call(e)) || e);
                toast.error(((_h = context.$L) === null || _h === void 0 ? void 0 : _h.call(context, 'uxp-core.auth-helper.login-failed', { error: e.message })) || ('Unable to login. ' + e.message));
                throw e;
            }
        });
    }
    function updateSession(token) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Load this dummy page to set the session
                const url = '/Apps/UXP/session?apikey=' + token;
                yield axios.get(url);
                // Don't reload - let context update propagate through parent's onAPIKeyChange callback
            }
            catch (e) {
                console.error('AUTH HELPER:: unable to update the session, Exception ', e);
                // Still continue - don't block on session update failure
            }
        });
    }
    function handleKeyDown(e) {
        if (e.key === 'Enter') {
            login();
        }
    }
    return _jsx(Modal, { show: show, onClose: () => __awaiter(void 0, void 0, void 0, function* () {
        }), backgroundDismiss: false, className: 'uxpcore_authhelper__modal', autoSize: true, children: _jsxs("div", { className: "uxpcore_authhelper", children: [_jsxs("div", { className: "uxpcore_authhelper__profile", children: [_jsx(ProfileImage, { name: (userDetails === null || userDetails === void 0 ? void 0 : userDetails.FullName) || '', image: (userDetails === null || userDetails === void 0 ? void 0 : userDetails.ProfileImageName) ? '/AccountResources/profilepics/' + userDetails.ProfileImageName : '', size: '10rem' }), _jsx("div", { className: "uxpcore_authhelper__name", children: (userDetails === null || userDetails === void 0 ? void 0 : userDetails.FullName) || '' })] }), _jsx("div", { className: 'uxpcore_authhelper__description', children: ((_a = context.$L) === null || _a === void 0 ? void 0 : _a.call(context, 'uxp-core.auth-helper.description')) || 'You have been logged out\nPlease enter your password and login to continue' }), _jsx(FormField, { children: _jsx(Input, { type: 'password', value: password, onChange: setPassword, placeholder: ((_b = context.$L) === null || _b === void 0 ? void 0 : _b.call(context, 'uxp-core.auth-helper.password-placeholder')) || 'Enter your password', onKeyDown: handleKeyDown }) }), _jsx("div", { className: 'uxpcore_authhelper__actions', children: _jsx(ButtonComponent, { title: ((_c = context.$L) === null || _c === void 0 ? void 0 : _c.call(context, 'uxp-core.text.login')) || 'Login', loadingTitle: ((_d = context.$L) === null || _d === void 0 ? void 0 : _d.call(context, 'uxp-core.text.authenticating')) || 'Authenticating...', onClick: login, variant: "primary" }) })] }) });
});
export default AuthHelperComponent;
